import React, { createContext, useContext, useState, useEffect } from "react";
import { authType, AUTH_TYPE_DEVELOPMENT } from "@/config/auth";
import { api } from "@/services/api";
import { User } from "@/types/api";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (
    email?: string,
    password?: string,
    devRole?: "admin" | "user"
  ) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  getAccessToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function AuthProviderContent({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Set up the API token refresh callback
  useEffect(() => {
    api.setTokenRefreshCallback(getAccessToken);
  }, []);

  // Check for existing authentication on mount
  useEffect(() => {
    const checkExistingAuth = async () => {
      if (authType === AUTH_TYPE_DEVELOPMENT) {
        const token = localStorage.getItem("authToken");
        if (token) {
          try {
            // Verify the token is still valid by making a test API call
            const response = await api.validateToken();
            if (response.success && response.data) {
              setUser(response.data.user);
            } else {
              // Token is invalid, clear it
              localStorage.removeItem("authToken");
              localStorage.removeItem("devRole");
            }
          } catch (error) {
            // Token is invalid, clear it
            localStorage.removeItem("authToken");
            localStorage.removeItem("devRole");
          }
        }
      }
      setIsLoading(false);
    };

    checkExistingAuth();
  }, []);

  // Get access token
  const getAccessToken = async (): Promise<string | null> => {
    if (authType === AUTH_TYPE_DEVELOPMENT) {
      return localStorage.getItem("authToken");
    }
    return null;
  };

  const login = async (
    email?: string,
    password?: string,
    devRole?: "admin" | "user"
  ) => {
    if (authType === AUTH_TYPE_DEVELOPMENT) {
      const response = await api.login(
        { email: email || "", password: password || "" },
        devRole
      );
      if (response.success && response.data) {
        const { token, user } = response.data;
        localStorage.setItem("authToken", token);
        setUser(user);
        if (devRole) {
          localStorage.setItem("devRole", devRole);
        }
      }
    } else {
      throw new Error("Authentication not configured for this environment");
    }
  };

  const logout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("devRole");
    setUser(null);
    window.location.href = "/";
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        logout,
        isAuthenticated,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  return <AuthProviderContent>{children}</AuthProviderContent>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
